import React, { useCallback } from 'react'
import { ReactComponent as GoogleIcon } from './../../images/icons/audience_google.svg'
import { ReactComponent as FacebookIcon } from './../../images/icons/audience_facebook.svg'
import { Button } from '@demandlocal/components'
import { useModal } from '../../components/useModal'
import { Audience } from '../../lib/types/Audience'
import { formatNumber } from '../../lib/utils/formatNumber'
import { useGetAudienceById } from '../../api/dealership/customers/audiences/useGetAudienceById'
import { motion } from 'framer-motion'
import { AudienceLink } from '../../lib/types/AudienceLink'
import { useDeleteAudienceById } from '../../api/dealership/customers/audiences/useDeleteAudienceById'
import { toast } from 'react-hot-toast'
import { useDealership } from '../../store/zustand/dealershipStore'
import { ReactComponent as WarningIcon } from '../../images/icons/warning.svg'
import { queryClient } from '../../api/queryClient'

type Props = {
  audience: Audience
}

const DeleteAudienceModal = ({ audience }: Props): JSX.Element => {
  const dealership = useDealership()
  const { closeModal } = useModal()
  const { data, isLoading: isLoadingAudiences } = useGetAudienceById(audience._id)
  const { mutateAsync: deleteAudience, isLoading } = useDeleteAudienceById()

  const onDeleteAudiencePress = useCallback(
    async (e) => {
      e.preventDefault()
      await deleteAudience({ audience_id: audience._id }).then(() => {
        closeModal()
        queryClient.refetchQueries(['dealership', dealership?.id, 'audiences'])
        toast.success('Audience successfully deleted!', { position: 'bottom-left' })
      })
    },
    [audience._id, closeModal, dealership?.id, deleteAudience],
  )

  return (
    <div className={'flex flex-1 flex-col px-12 pt-12 font-Spartan h-full'}>
      <div className={'flex flex-1 flex-col overflow-hidden'}>
        <div className={'font-bold text-blue-primary text-[28px]'}>
          Are you sure you want to delete this audience?
        </div>
        <div className={'flex-1 flex items-center'}>
          <div className={'flex flex-1 flex-col gap-5'}>
            <div className={'pb-5 border-b-2 flex-1 flex flex-col'}>
              <span className={'text-blue-primary font-bold text-sm'}>Audience name</span>
              <span className={'mt-3 font-medium text-base'}>
                {audience.audience_name}
              </span>
            </div>
            <div className={'pb-5 border-b-2 flex-1 flex flex-col'}>
              <span className={'text-blue-primary font-bold text-sm'}>Description</span>
              <span className={'mt-3 font-medium text-base'}>{audience.description}</span>
            </div>
            <div className={'pb-5 border-b-2 flex-1 flex flex-col'}>
              <span className={'text-blue-primary font-bold text-sm'}>Customers</span>
              <span className={'mt-3 font-medium text-base'}>
                {formatNumber(audience.audience_size)}
              </span>
            </div>
            {!isLoadingAudiences && (data?.audienceLinks?.length ?? 0) > 0 && (
              <div className={'pb-5 flex-1 flex flex-col'}>
                <span className={'text-blue-primary font-bold text-sm'}>Linked to</span>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ type: 'spring', mass: 1, damping: 50, stiffness: 500 }}
                  className={'overflow-hidden'}>
                  <div
                    className={
                      'mt-3 flex flex-row flex-1 flex-wrap justify-start items-end gap-3'
                    }>
                    {data?.audienceLinks?.map((value: AudienceLink, index: number) => {
                      return (
                        <motion.div
                          key={`${index}`}
                          className={
                            'flex flex-row p-2 min-w-32 rounded-lg min-w-[120px] bg-gainsboro-gray'
                          }>
                          {value.vendor_id === 1 && <GoogleIcon />}
                          {value.vendor_id === 3 && <FacebookIcon />}
                          <div
                            className={
                              'font-bold flex flex-col justify-between ml-3 text-xs'
                            }>
                            <span>{value.vendor_account_vendor_account_name}</span>
                            <span className={'font-medium pt-1 text-xs'}>
                              {value.vendor_account_vendor_id}
                            </span>
                          </div>
                        </motion.div>
                      )
                    })}
                  </div>
                </motion.div>
              </div>
            )}
          </div>
        </div>
        <div className={'mb-10 text-primary-blue font-medium text-xs'}>
          <div
            className={
              'bg-gray-100 p-2 rounded-md w-full flex flex-row items-center gap-2 bg-secondary-bittersweet-red/50 text-secondary-engineering-red'
            }>
            <WarningIcon className="min-h-6 min-w-6" />
            <span className="font-bold">
              If you delete this audience it doesn't get removed from Google/Facebook
              automatically. This is because the audience might be used in campaigns that
              we don't have access to, or you might've created them using a different
              account than the one you're currently using.
            </span>
          </div>
        </div>
      </div>
      <div className={'h-20 items-center flex justify-between border-t-2 py-4 mt-4'}>
        <Button
          variant={'secondary'}
          onClick={(e) => {
            e.preventDefault()
            closeModal()
          }}>
          Close
        </Button>
        <Button disabled={isLoading} type={'submit'} onClick={onDeleteAudiencePress}>
          Delete and unlink audience
        </Button>
      </div>
    </div>
  )
}

export default DeleteAudienceModal
